import { useState } from 'react';
import toast from 'react-hot-toast';
import * as HOOKS from '@siloreal:shared:hooks';

type ResolveSilobagIds = () => Promise<readonly string[]>;

export function useLabTemplateDownload() {
  const [isResolving, setIsResolving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [silobagIds, setSilobagIds] = useState<string[]>([]);
  const i18n = HOOKS.useLocale.getState();

  const open = async (resolveSilobagIds: ResolveSilobagIds): Promise<void> => {
    if (isResolving) return;

    setIsResolving(true);
    const loadingToastId = toast.loading(
      i18n.t('temp.common.toasts.loading.preparing_quality_sample_spreadsheet')
    );

    try {
      const resolvedIds = await resolveSilobagIds();
      const normalizedIds = [
        ...new Set(
          resolvedIds
            .map(String)
            .map((id) => id.trim())
            .filter(Boolean)
        ),
      ];

      if (normalizedIds.length === 0) {
        throw new Error(i18n.t('temp.common.error.no_available_silobags_for_spreadsheet'));
      }

      setSilobagIds(normalizedIds);
      setIsModalOpen(true);
      toast.dismiss(loadingToastId);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : i18n.t('temp.common.error.failed_to_prepare_laboratory_templates');
      toast.error(message, { id: loadingToastId });
    } finally {
      setIsResolving(false);
    }
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setSilobagIds([]);
  };

  return {
    isResolving,
    isModalOpen,
    silobagIds,
    open,
    closeModal,
  };
}
